import {BI_ORIGINS, PlaceWithTicketInfo, isAvailableDonationTicket} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {ChevronLeftSmall, ChevronRightSmall, DeleteSmall} from '@wix/wix-ui-icons-common/on-stage'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {TextButton} from 'wix-ui-tpa/cssVars'
import {useColors} from '../../../../../hooks/colors'
import {getPlaceInfoText, isPlaceArea, isPlaceInBasket} from '../../../../../selectors/seating/place'
import {isDonationValid} from '../../../../../services/donations'
import {StyledButton} from '../../styled-button'
import {TicketAreaCounter} from '../ticket-area-counter'
import {TicketDonation} from '../ticket-donation'
import {TicketFees} from '../ticket-fees'
import {classes} from './ticket-action.st.css'
import {TicketActionProps} from './index'

export const TicketAction = ({
  showAccessibilityMode,
  inDescription,
  inDrawer,
  onClick,
  place,
  selectedTicketQuantity,
  chooseSeatMode,
  chooseMultipleSeatsMode,
  scheduled,
  setPlaceQuantity,
  updatePlace,
  description,
  isPlaceSelected,
  basketView = false,
}: TicketActionProps) => {
  const {isRTL} = useEnvironment()
  const {darkColor} = useColors()
  const {t} = useTranslation()
  const placeInfoText = getPlaceInfoText(place, t)
  const area = isPlaceArea(place)
  const inBasket = isPlaceInBasket(place)
  const draftState = area && inDrawer

  const [amount, setAmount] = useState(1)
  const placeInBasket = useRef<PlaceWithTicketInfo>(null)

  useEffect(() => {
    if (draftState) {
      placeInBasket.current = place
      return () => {
        if (placeInBasket.current) {
          updatePlace({place: placeInBasket.current})
        }
      }
    }
  }, [draftState, updatePlace, place.id])

  useEffect(() => {
    if (area) {
      setAmount(showAccessibilityMode || selectedTicketQuantity ? selectedTicketQuantity : 1)
    }
  }, [showAccessibilityMode, selectedTicketQuantity, area])

  const addToBasket = useCallback(
    (addedPlace: PlaceWithTicketInfo, count: number) => {
      setPlaceQuantity({
        placeId: addedPlace.id,
        count,
        origin: showAccessibilityMode ? BI_ORIGINS.SEATINGS_MODAL_TICKET_LIST : BI_ORIGINS.SEATINGS_MODAL_BASKET,
      })
    },
    [showAccessibilityMode, setPlaceQuantity],
  )

  const clearBasketBackup = useCallback(({ticket, donation}: PlaceWithTicketInfo) => {
    if (isDonationValid(ticket, donation)) {
      placeInBasket.current = null
    }
  }, [])

  if (scheduled || inDescription) {
    return null
  }

  const addButtonText = inBasket ? t('seatings_removeFromBasket') : t('seatings_addToBasket')

  return (
    <>
      {isAvailableDonationTicket(place.ticket) ? (
        <>
          <TicketDonation place={place} />
          <TicketFees place={place} />
        </>
      ) : null}
      {area && !chooseSeatMode ? (
        <TicketAreaCounter
          value={amount}
          setValue={draftState ? setAmount : count => addToBasket(place, count)}
          place={place}
          showAccessibilityMode={showAccessibilityMode}
        />
      ) : null}
      {showAccessibilityMode && (!area || chooseSeatMode || chooseMultipleSeatsMode) ? (
        <TextButton
          data-hook={DH.ADD}
          className={classNames({
            [classes.addButton]: !inBasket,
            [classes.removeButton]: inBasket,
            [classes.buttonNotWired]: !darkColor,
          })}
          prefixIcon={inBasket ? <DeleteSmall /> : undefined}
          aria-label={`${addButtonText} ${placeInfoText}`}
        >
          {addButtonText}
        </TextButton>
      ) : null}
      {basketView && description ? (
        <TextButton
          data-hook={DH.MORE_BUTTON}
          className={classNames(classes.moreInfo, {
            [classes.buttonNotWired]: !darkColor,
            [classes.moreInfoWithCounter]: area,
          })}
          suffixIcon={isRTL ? <ChevronLeftSmall /> : <ChevronRightSmall />}
          onClick={onClick}
          aria-label={`${t('seatings_ticket_moreInfo')} ${placeInfoText}`}
        >
          {t('seatings_ticket_moreInfo')}
        </TextButton>
      ) : null}
      {inDrawer && isPlaceSelected ? (
        <StyledButton
          key="add-to-basket-button"
          className={classes.buttonNoMargin}
          dataHook={DH.ADD}
          onClick={() => {
            clearBasketBackup(place)
            addToBasket(place, amount)
          }}
        >
          {t('seatings_add')}
        </StyledButton>
      ) : null}
    </>
  )
}
